import { LoadingButton } from '@mui/lab';
import { Box, CircularProgress, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { STATE_DRAFT, STATE_PUBLISHED, STATE_UNPUBLISHED } from '@opengov/stories-shared/constants';
import { IPublication, IPublicationNavMenuItem } from '@opengov/stories-shared/types';

import React, { useEffect, useState } from 'react';

import { IOnlinePublicationMessages, ISessionUser } from '../../../../../types';

import { useSetupOnlinePublicationUi } from '../context/SetupOnlinePublicationUi.context';
import { useGenerateOnlinePublicationUrl } from '../hooks/useGenerateOnlinePublicationUrl';
import { SetupOnlinePublicationWarning } from '../../../../../modals/components/SetupOnlinePublicationWarning';
import { SharePublication } from '../../../../../modals/components/SharePublication';
import { SharePublicationLink } from '../../../../../modals/components/SharePublicationLink';
import { modals as modalMessages } from '../../../../../locale/stories.en.locale.json';

import { styles } from './Header.styles';

export type IHeaderProps = {
  isPreviewAvailable: boolean;
  isAdmin: boolean;
  loadingPublicationBrandings: boolean;
  loadingTocSettings: boolean;
  publicationId: string;
  publicationNavigationMenuItems: IPublicationNavMenuItem[];
  entitySubdomain: string;
  messages: IOnlinePublicationMessages;
  publication: IPublication;
  user: ISessionUser;
  enableGoogleTranslateForPublications: boolean;
  navigateToPreviewOnlinePublication(entitySubdomain: string, publicationId: string): void;
  navigateToPublicationView(entitySubdomain: string, publicationId: string): void;
}

export const Header: React.FC<IHeaderProps> = ({
  entitySubdomain,
  isPreviewAvailable,
  loadingPublicationBrandings,
  loadingTocSettings,
  messages,
  publication: publicationDetails,
  user,
  publicationId,
  navigateToPublicationView,
  isAdmin,
  publicationNavigationMenuItems,
  enableGoogleTranslateForPublications
}) => {
  const { state: uiState, dispatch: uiDispatch } = useSetupOnlinePublicationUi();
  const { isOrganizationMenuOrStoriesInProgress, updatePublicationPreview } = uiState;
  const generatePreviewMutation = useGenerateOnlinePublicationUrl({ entitySubdomain });
  const generateShareMutation = useGenerateOnlinePublicationUrl({ entitySubdomain });

  const isSaving =
    isOrganizationMenuOrStoriesInProgress ||
    loadingPublicationBrandings ||
    loadingTocSettings ||
    generatePreviewMutation.isLoading ||
    updatePublicationPreview;

  const [isGeneratingPreviewLink, setIsGeneratingPreviewLink] = useState(false);
  const [isNavMenuNotSetUpOpen, setIsNavMenuNotSetUpOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isShareLinkOpen, setIsShareLinkOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const totalStoriesCount = publicationNavigationMenuItems?.reduce(
    (storiesCount: number, menuItem: IPublicationNavMenuItem) =>
      storiesCount + menuItem?.publicationNavigationMenuStories?.length || 0,
    0
  );

  const showPublicationPreview = (openUrl = false) => {
    setIsGeneratingPreviewLink(true);
    void generatePreviewMutation
      .mutateAsync({
        publication: publicationDetails,
        userId: user.id,
        update: true,
        enableGoogleTranslateForPublications,
        isPreview: true
      })
      .then((url) => {
        uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: false });
        setIsGeneratingPreviewLink(false);
        if (url && openUrl) {
          window.open(url, '_blank');
        }
      })
      .catch(() => {
        uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: false });
        setIsGeneratingPreviewLink(false);
      });
  };

  useEffect(() => {
    if (updatePublicationPreview) showPublicationPreview();
    // `showPublicationPreview` depends on entity/user fields that don't change
    // inside this view; depending only on the trigger flag avoids re-firing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePublicationPreview]);

  const hasNavMenuStories = !!(publicationNavigationMenuItems.length && totalStoriesCount);

  // Draft Share: open the Share Publication modal when the nav menu has stories,
  // otherwise warn. (main opened SHARE_PUBLICATION / SETUP_ONLINE_PUBLICATION_WARNING.)
  const openSharePublicationFlow = () => {
    if (hasNavMenuStories) {
      setIsShareOpen(true);
    } else {
      setIsNavMenuNotSetUpOpen(true);
    }
  };

  // Generate the shareable link from the Share modal, then show the link modal.
  const confirmGenerateShareLink = () => {
    void generateShareMutation
      .mutateAsync({
        publication: publicationDetails,
        userId: user.id,
        update: true,
        enableGoogleTranslateForPublications,
        isPreview: false,
        notifyAdmins: true
      })
      .then((url) => {
        setIsShareOpen(false);
        setShareUrl(url ?? '');
        setIsShareLinkOpen(true);
      })
      .catch(() => undefined);
  };

  // Published/Unpublished "Update link"/"Reshare": regenerate the link directly.
  const generateSharePublicationUrl = () => {
    if (hasNavMenuStories) {
      void generateShareMutation.mutateAsync({
        publication: publicationDetails,
        userId: user.id,
        update: true,
        enableGoogleTranslateForPublications,
        isPreview: false,
        notifyAdmins: true
      });
    } else {
      setIsNavMenuNotSetUpOpen(true);
    }
  };

  const renderPrimaryButton = () => {
    if (publicationDetails?.status === STATE_DRAFT) {
      return (
        <LoadingButton
          loading={generateShareMutation.isLoading}
          data-test="share-publication"
          variant="contained"
          sx={styles.button}
          onClick={openSharePublicationFlow}
        >
          {messages.share}
        </LoadingButton>
      );
    } else if ([STATE_PUBLISHED, STATE_UNPUBLISHED].includes(publicationDetails?.status)) {
      return (
        <LoadingButton
          loading={generateShareMutation.isLoading}
          data-test={publicationDetails?.status === STATE_UNPUBLISHED ? 'reshare-publication' : 'update-publication'}
          variant="contained"
          sx={styles.button}
          onClick={generateSharePublicationUrl}
        >
          {messages.updateLink}
        </LoadingButton>
      );
    }
  };

  const renderLoadingSpinner = () => {
    return (
      <Box sx={styles.loadingBlock}>
        <CircularProgress size={16} role="status" aria-label={messages.updatingPreview} />
        <Typography variant="body2" sx={styles.publicationName}>
          {messages.updatingPreview}...
        </Typography>
      </Box>
    );
  };

  const renderDoneButton = () => {
    return (
      <LoadingButton
        disabled={isSaving}
        data-test="done-online-publication-button"
        variant="contained"
        sx={styles.button}
        onClick={() => {
          uiDispatch({ type: 'SET_SELECTED_MENU_ID', menuId: null });
          void navigateToPublicationView(entitySubdomain, publicationId);
        }}
      >
        {messages.done}
      </LoadingButton>
    );
  };

  const renderPreviewButton = () => {
    return (
      <LoadingButton
        loading={isGeneratingPreviewLink}
        data-test="preview-online-publication-button"
        disabled={isSaving || !isPreviewAvailable}
        variant="outlined"
        color="secondary"
        sx={styles.button}
        onClick={() => {
          showPublicationPreview(true);
        }}
      >
        <VisibilityIcon aria-hidden={true} sx={styles.icon} />
        {messages.preview}
      </LoadingButton>
    );
  };

  return (
    <>
      <Box component="header" sx={styles.root} role="banner">
        <Box sx={styles.content}>
          <Box sx={styles.headerText}>
            <Typography variant="h6" className="title" sx={styles.title}>
              {messages.setupOnlinePublication}
            </Typography>
            {publicationDetails.title && (
              <Typography variant="body2" className="publicationName" sx={styles.publicationName}>
                {messages.publicationName({ name: publicationDetails.title })}
              </Typography>
            )}
          </Box>
          <Box sx={styles.buttons}>
            {isSaving && renderLoadingSpinner()}
            {renderPreviewButton()}
            {renderDoneButton()}
            {isAdmin && renderPrimaryButton()}
          </Box>
        </Box>
      </Box>
      <SetupOnlinePublicationWarning
        open={isNavMenuNotSetUpOpen}
        onClose={() => setIsNavMenuNotSetUpOpen(false)}
        messages={modalMessages}
      />
      <SharePublication
        open={isShareOpen}
        isLoading={generateShareMutation.isLoading}
        onClose={() => setIsShareOpen(false)}
        onConfirm={confirmGenerateShareLink}
        messages={modalMessages}
      />
      <SharePublicationLink
        open={isShareLinkOpen}
        publicUrl={shareUrl}
        onClose={() => setIsShareLinkOpen(false)}
        messages={modalMessages}
      />
    </>
  );
};
